import Logo from "@/components/Logo";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] py-16 px-6">
      <div className="max-w-2xl mx-auto">
        <a href="/" className="font-heading font-black text-xl uppercase tracking-tight text-[#1D3557] inline-block mb-10 flex items-center gap-1">
          <Logo size={28} />
          <span>target<span className="text-[#E63946]">funder</span><span className="text-[#1D3557]/30">.com</span></span>
        </a>

        <h1 className="font-heading font-bold text-3xl uppercase tracking-tight text-[#1D3557] mb-2" data-testid="terms-title">
          Service Terms & Conditions
        </h1>
        <p className="font-body text-xs text-[#1D3557]/40 mb-8">Last updated: April 2026</p>

        <div className="font-body text-sm text-[#1D3557]/70 leading-relaxed space-y-6">
          <div>
            <h2 className="font-bold text-[#1D3557] text-base mb-2">TargetFunder Service Agreement</h2>
            <p>TargetFunder is a trading name of <strong className="text-[#1D3557]">Castleview Resources Ltd</strong>, Unit 3 Grange Road Retail Park, Dublin 16, D16 XD82, Ireland. VAT ID: IE4271347SH.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">1. Services Provided</h3>
            <p className="mb-2">TargetFunder provides a hosted, white-label fundraising platform for sports clubs. Each club receives:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>A branded fundraiser page customised with the club's name, logo, and colours</li>
              <li>A club admin dashboard to manage entries, view the leaderboard, and edit prizes</li>
              <li>Stripe Connect payment integration — entry fees are paid directly to the club's bank account</li>
              <li>Automated confirmation emails to entrants</li>
              <li>GDPR-compliant Terms & Conditions and Privacy Policy, branded to the club</li>
            </ul>
            <p className="mt-2">The platform is provided for a fixed term per event (typically 3–6 weeks). This is a technical service only.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">2. Onboarding & Activation</h3>
            <p className="mb-2">The onboarding process is as follows:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>The club previews their branded site via our free demo at targetfunder.com.</li>
              <li>The club activates by completing a short onboarding form and paying the one-off setup fee.</li>
              <li>After payment, the club completes Stripe Connect onboarding — either by creating a new Stripe account (guided walkthrough provided) or connecting an existing club Stripe account via OAuth.</li>
              <li>Once Stripe approves the club's KYC verification, the public fundraiser page goes live automatically and the club admin receives a one-click "magic-link" email to access the dashboard.</li>
            </ol>
            <p className="mt-2">TargetFunder monitors new activations and reserves the right to suspend any account that appears fraudulent, misrepresents the club, or is not operating in accordance with applicable laws.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">3. Pricing & Payment</h3>
            <p className="mb-2">The club selects one of the following pricing options at the time of activation:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li><strong className="text-[#1D3557]">Pay per entry (€0 upfront):</strong> TargetFunder charges a fixed fee per entry (currently €1 / £1), which covers all TargetFunder fees and payment processing. The remaining entry fee amount is paid directly to the club's bank account via Stripe. No upfront payment is required.</li>
              <li><strong className="text-[#1D3557]">One-off setup fee (€249 / £195):</strong> A one-time payment. After payment, the club keeps 100% of all entry fees collected — no per-entry fees are applied by TargetFunder. Only standard Stripe payment processing applies (typically 1.5% + €0.25 in Ireland, 1.5% + 20p in the UK).</li>
            </ul>
            <p className="mb-2">Refund policy:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>For the one-off fee option: the fee is non-refundable once payment has been processed, except where Stripe is unable to verify the club's details and the club's public page has not gone live, or where TargetFunder fails to deliver the service within a reasonable timeframe.</li>
              <li>For the pay-per-entry option: no upfront payment is made, so no refund applies. Per-entry fees are non-refundable once processed.</li>
            </ul>
            <p className="mt-2">No ongoing subscription fees or hidden charges are applied by TargetFunder under either pricing model.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">4. Club Responsibilities (The Operator)</h3>
            <p className="mb-2">The club is the sole legal "Promoter" and "Operator" of the fundraiser. The club is responsible for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Ensuring the fundraiser complies with all applicable local laws and regulations (e.g., obtaining a Small Society Lottery license or equivalent, if required)</li>
              <li>Providing accurate club details during the onboarding process</li>
              <li>Setting up and maintaining an active, compliant Stripe Connect account to receive funds</li>
              <li>Prize fulfilment and handling all member queries and disputes</li>
              <li>Ensuring all persons entering the competition are eligible to do so</li>
              <li>Keeping the registered admin email secure — access to the admin dashboard is granted via magic-link emails sent to that address, so anyone with access to that inbox can access the dashboard</li>
              <li>Warranting that the club has the right to display any sponsor logos or third-party brand assets uploaded to its fundraiser page</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">5. TargetFunder's Role</h3>
            <p className="mb-2">TargetFunder is a technology provider only. We are not a promoter, professional fundraiser, lottery operator, or payment processor. We do not handle, hold, or have access to any funds raised by the club. All entry fee payments are processed by Stripe and deposited directly into the club's designated bank account.</p>
            <p>TargetFunder monitors new activations and may contact the club or suspend service where account details appear inconsistent with a legitimate sports club.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">6. Verification</h3>
            <p>Identity and bank verification is performed by our payment partner Stripe as part of their KYC process. This includes photo ID, address, and bank account verification. TargetFunder reserves the right to suspend any club where Stripe verification fails, or where TargetFunder has reasonable grounds to suspect misrepresentation or fraudulent intent.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">7. Intellectual Property</h3>
            <p className="mb-2">TargetFunder grants the club a limited, non-exclusive, non-transferable license to use the platform for the duration of the event. All software, code, designs, and platform infrastructure remain the sole property of TargetFunder.</p>
            <p>All club fundraiser pages will display a "Made by TargetFunder" badge. This badge may not be removed or obscured.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">8. Marketing Permission</h3>
            <p>By completing the onboarding process, the club grants TargetFunder permission to reference the club's name, logo, and competition in TargetFunder's own marketing materials, website, and social media channels. This permission may be withdrawn by the club at any time by written request to <a href="mailto:enquiries@targetfunder.com" className="text-[#1D3557] underline">enquiries@targetfunder.com</a>.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">9. Limitation of Liability</h3>
            <p>TargetFunder's total liability for any claim arising from or related to this service shall not exceed the amount of the setup fee paid by the club. TargetFunder is not liable for any loss of fundraising revenue, indirect or consequential losses, or any losses arising from technical downtime, third-party service failures (including Stripe), or the club's failure to comply with applicable laws.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">10. Termination</h3>
            <p>TargetFunder reserves the right to suspend or terminate a club's fundraiser page at any time if the club is found to be in breach of these terms, operating fraudulently, or in violation of applicable laws. In such cases, no refund of the setup fee shall be due.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">11. Governing Law</h3>
            <p>This agreement is governed by and construed in accordance with the laws of Ireland. Any disputes arising under this agreement shall be subject to the exclusive jurisdiction of the courts of Ireland.</p>
          </div>

          <hr className="border-[#1D3557]/10 my-8" />

          <div>
            <h2 className="font-bold text-[#1D3557] text-base mb-4">TargetFunder Data Processing Agreement</h2>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">1. Roles & Definitions</h3>
            <p>The club is the Data Controller (the owner and decision-maker regarding member data). TargetFunder is the Data Processor (processing data on the club's behalf and under the club's instruction).</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">2. Data Collected</h3>
            <p className="mb-2">The following personal data is collected from competition entrants:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Payment information (processed by Stripe — TargetFunder does not store card details)</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">3. Purpose of Processing</h3>
            <p>Personal data is processed solely for the purposes of operating the competition, maintaining the leaderboard on the club's public page, sending entry confirmation emails (via Resend), and facilitating entry fee payments (via Stripe).</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">4. Data Security</h3>
            <p>TargetFunder implements appropriate technical and organisational measures to protect member data, including encrypted data transmission (TLS/SSL), secure database storage, and access controls limiting data access to authorised personnel only.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">5. Data Retention & Deletion</h3>
            <p>TargetFunder retains member personal data for the duration of the competition and for 30 days following its conclusion, to allow for prize distribution and any tournament-related correspondence. After this period, data subjects may request deletion of their personal data at any time by contacting the club. Anonymised, aggregated data may be retained for analytical purposes.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">6. Sub-processors</h3>
            <p className="mb-2">TargetFunder uses the following third-party sub-processors:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-[#1D3557]">Stripe</strong> — payment processing</li>
              <li><strong className="text-[#1D3557]">Resend</strong> — transactional email delivery</li>
              <li><strong className="text-[#1D3557]">MongoDB Atlas</strong> — database hosting</li>
              <li><strong className="text-[#1D3557]">Railway</strong> — application hosting</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">7. Data Subject Rights</h3>
            <p>TargetFunder will assist the club in responding to data subject access requests, deletion requests, or other rights exercised under GDPR or applicable data protection legislation. Requests should be directed to <a href="mailto:enquiries@targetfunder.com" className="text-[#1D3557] underline">enquiries@targetfunder.com</a>.</p>
          </div>

          <div>
            <h3 className="font-bold text-[#1D3557] mb-1">8. Club's Obligations</h3>
            <p>The club warrants that it has the necessary legal basis (e.g., "Performance of a Contract") to collect and process member data for the purposes of the competition. The club is responsible for informing its members about how their data will be used.</p>
          </div>

          <div className="pt-6 border-t border-[#1D3557]/10">
            <p className="text-xs text-[#1D3557]/40">
              For any queries regarding these terms, please contact us at{" "}
              <a href="mailto:enquiries@targetfunder.com" className="text-[#1D3557] underline">enquiries@targetfunder.com</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
